/* 카페24 UX 맞춤 — 값이 아니라 동작을 맞추는 스크립트
 *
 * ① 헤더 최상위 메뉴를 **클릭하면 이동**하게 한다.
 * ② 정렬 select 의 폭을 **선택된 글자 폭에 맞춘다**(iOS 대응 — 아래 주석).
 */
(() => {
  "use strict";

  /* ── 헤더 최상위 메뉴를 링크처럼 ────────────────────────────────
   * 카페24 헤더의 `Shop` 은 하위 메뉴가 없고 **누르면 바로 카테고리로 이동**한다
   * (`/product/list.html?cate_no=24`). Dawn 은 하위가 있으면 `<summary>` 가 되어
   * 클릭이 드롭다운 토글로만 쓰인다 — 눌러도 아무 데도 가지 않는다.
   *
   * ⚠️ `<summary>` 를 `<a>` 로 바꾸지 않는다. `<details>` 의 구조가 깨지고
   *    키보드·스크린리더 동작을 잃는다. 클릭만 가로챈다.
   * ⚠️ 이동 대상은 **첫 하위 링크**다. 카페24가 `Shop` 을 첫 카테고리로 보내는 것과 같다.
   *    Shopify 메뉴에서는 NEW ARRIVALS 가 첫 항목이다.
   *
   * ⚠️⚠️ **드롭다운 패널은 CSS 가 숨긴다**(`neoteria-cafe24-parity.css`, ≥990px).
   *    처음엔 "하위 카테고리가 4개라 없애면 이동 경로가 줄어든다"며 hover 드롭다운을
   *    **일부러 남겼는데**, 카페24 데스크톱 GNB 에는 그 패널이 아예 없다(제보로 확인).
   *    데스크톱에서 카테고리로 가는 길은 **목록 페이지의 카테고리 탭**이다 — 경로가
   *    줄어드는 게 아니라 카페24와 같은 곳에 있다.
   *
   * ⚠️ **그래서 키보드도 이동시킨다.** 패널이 숨겨진 뒤로는 Enter/Space 로 열어도
   *    보이는 게 없어, 키보드 사용자만 SHOP 에서 아무 데도 못 가는 상태가 됐다.
   *    (패널을 남겨 뒀을 때는 `event.detail === 0` 으로 키보드를 제외하는 것이 맞았다.)
   */
  const wire = (summary) => {
    if (summary.dataset.ntLinked) return;
    summary.dataset.ntLinked = "1";

    summary.addEventListener("click", (event) => {
      const details = summary.parentElement;
      if (!details) return;
      const first = details.querySelector(
        ".mega-menu__link, .header__submenu a, ul a"
      );
      if (!first) return;
      const href = first.getAttribute("href");
      if (!href) return;
      event.preventDefault();
      window.location.href = href;
    });
  };

  /* ── 정렬 select 폭을 글자에 맞춘다 ────────────────────────────
   * ⚠️⚠️ **iOS Safari 는 `<select>` 의 `text-align` 을 무시한다** — 닫힌 select 에 그려지는
   *    글자는 항상 왼쪽이다. 그래서 CSS 로 `text-align: end` → `right` 까지 바꿔 봤지만
   *    (2026-09-09) iOS 에서는 여전히 163px 박스 왼쪽에 글자가 남아 화살표와 멀었다.
   *    Chrome 은 두 값 모두 적용돼 우측 정렬이었다 — 그래서 화면마다 결과가 갈렸다.
   * ⚠️ 그래서 **정렬에 기대지 않는다.** select 박스를 선택된 글자 폭 + 오른쪽 여백만큼으로
   *    줄이면, 글자가 왼쪽에 그려지든 오른쪽에 그려지든 **화살표와의 간격이 같다**.
   *    (`padding-right: 16px` = 화살표 10 + 빈틈 6 — parity CSS 의 값이다.)
   * ⚠️ 박스를 줄여도 화면은 그대로다 — select 는 배경·보더가 없고(투명) 행이
   *    `justify-content: flex-end` 라 **오른쪽 끝에 붙은 채로** 폭만 준다.
   * ⚠️⚠️ **웹폰트가 로드된 뒤 다시 재야 한다.** 첫 측정이 폴백 글꼴로 이뤄지면 폭이 어긋난다
   *    (`document.fonts.ready`).
   * ⚠️ 브레이크포인트마다 글자 크기가 다르다(PC 20px · 모바일 13px) — resize 에서 다시 잰다.
   * ⚠️ 옵션 문구에 앞뒤 공백이 있다(` Featured `) — trim 하지 않으면 폭이 커진다.
   * ⚠️ 폭을 지우지 말 것(`width: auto` 로 되돌리면 브라우저가 **가장 긴 옵션** 기준으로
   *    잡아 짧은 항목에서 다시 벌어진다).
   */
  let measure = null;

  const fitSort = (sel) => {
    const text = (sel.options[sel.selectedIndex] || {}).text;
    if (!text) return;
    const cs = getComputedStyle(sel);
    if (!measure) measure = document.createElement("canvas").getContext("2d");
    measure.font = [cs.fontStyle, cs.fontWeight, cs.fontSize, cs.fontFamily].join(" ");
    const w = measure.measureText(text.trim()).width;
    if (!w) return;
    const padR = parseFloat(cs.paddingRight) || 0;
    const padL = parseFloat(cs.paddingLeft) || 0;
    /* 반올림 오차로 글자가 잘리지 않게 1px 만 더한다 — 더 주면 그만큼 빈틈이 는다. */
    const next = Math.ceil(w + padR + padL + 1) + "px";
    /* ⚠️ 값이 같으면 쓰지 않는다 — 아래 MutationObserver 가 DOM 변화마다 부르므로
       매번 인라인 스타일을 쓰면 불필요한 레이아웃이 생긴다. */
    if (sel.style.width !== next) sel.style.width = next;
  };

  const fitAllSorts = () => {
    document.querySelectorAll("select.facet-filters__sort").forEach((sel) => {
      fitSort(sel);
      if (sel.dataset.ntSortFit) return;
      sel.dataset.ntSortFit = "1";
      sel.addEventListener("change", () => fitSort(sel));
    });
  };

  const init = () => {
    document
      .querySelectorAll(".header__inline-menu summary.header__menu-item")
      .forEach(wire);
    fitAllSorts();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitAllSorts);
  }

  /* Dawn 은 필터를 적용하면 `.facets-container` 를 통째로 다시 그린다 — 새 select 에도 붙인다. */
  document.addEventListener("shopify:section:load", init);

  let resizeTimer = 0;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fitAllSorts, 150);
  });

  if (window.MutationObserver) {
    new MutationObserver(fitAllSorts).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();
